import { CommonModule } from '@angular/common';
import { Component, NgModule, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { BaseComponent } from '../base.component';
import { ToastrService } from 'ngx-toastr';
import { TranslationService } from '@core/services/translation.service';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';

export interface GeneratePassword {
  userName: string;
  password: string;
  confirmPassword: string;
  code: string;
}

export interface GeneratePasswordRes {
  success: boolean;
  data: null;
  statusCode: number;
  errors: string[];
  errorMessages: string[];
  message: string;
}

@Component({
  selector: 'app-app-forgot-password',
  templateUrl: './app-forgot-password.component.html',
  styleUrls: ['./app-forgot-password.component.scss'],
})
export class AppForgotPasswordComponent
  extends BaseComponent
  implements OnInit
{
  public forgotPasswordForm: FormGroup = <FormGroup>{};
  isLoading = false;
  showThankyouText: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  constructor(
    private fb: FormBuilder,
    private toastrService: ToastrService,
    public translationService: TranslationService,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {
    super(translationService);
    this.createForgotPasswordForm();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((res) => {
      if (res && Object.keys(res).length > 0) {
        const email = res['username'];
        const code = res['code'];
        this.forgotPasswordForm.patchValue({
          userName: email,
          code: code,
        });
        this.forgotPasswordForm.get('userName').disable();
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  private createForgotPasswordForm(): FormGroup {
    return (this.forgotPasswordForm = this.fb.group(
      {
        userName: new FormControl(''),
        password: new FormControl('', [
          Validators.required,
          Validators.minLength(6),
        ]),
        confirmPassword: new FormControl('', [
          Validators.required,
          Validators.minLength(6),
        ]),
        code: new FormControl(''),
      },
      { validator: this.checkPasswords }
    ));
  }

  checkPasswords(group: FormGroup) {
    const pass = group.get('password').value;
    const confirmPass = group.get('confirmPassword').value;
    return pass === confirmPass ? null : { notSame: true };
  }

  changePassword() {
    if (this.forgotPasswordForm.valid) {
      const generatePass: GeneratePassword = {
        userName: this.forgotPasswordForm.get('userName').value,
        password: this.forgotPasswordForm.get('password').value,
        confirmPassword: this.forgotPasswordForm.get('confirmPassword').value,
        code: this.forgotPasswordForm.get('code').value,
      };
      if (generatePass) {
        this.isLoading = true;
        this.generatePassword(generatePass).subscribe({
          next: (res: GeneratePasswordRes) => {
            if (res && res.success) {
              this.toastrService.success(res.message);
              this.showThankyouText = true;
              this.isLoading = false;
            } else {
              if (res && res.errors && res.errors.length > 0) {
                this.toastrService.error(res.errors[0]);
                this.isLoading = false;
              } else if (
                res &&
                res.errorMessages &&
                res.errorMessages.length > 0
              ) {
                this.toastrService.error(res.errorMessages[0]);
                this.isLoading = false;
              }
            }
          },
          error: () => {
            this.isLoading = false;
          },
        });
      }
    }
  }

  private generatePassword(body: GeneratePassword) {
    const url = 'Authentication/ConfirmPassword';
    return this.http.post<GeneratePasswordRes>(url, body);
  }
}

@NgModule({
  declarations: [AppForgotPasswordComponent],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
})
export class AppForgotPasswordModule {}
